import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { REPORT_GROUPS, ACTIVITY_COLS, ENERGY_COLS, POI_VISITS_COLS, ROAD_SPEED_COLS } from "@/lib/taxonomy";
import { alertMeta, fmtTime } from "@/lib/fleet";
import ConfigPanel from "@/components/ConfigPanel";
import ScheduledReportsPanel from "@/components/ScheduledReportsPanel";
import ReportHistoryPanel from "@/components/ReportHistoryPanel";
import { FileBarChart, Cpu, Trash2, Activity, Gauge, Timer, TrendingUp, Fuel, Wrench, ChevronLeft, ChevronRight, ChevronDown, Download, CalendarClock, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { limitDays } from "@/lib/reportLimit";
import { getUserSettings } from "@/lib/userSettings";
import { summarizeRows, filterRowsByUnits, csvSafe, summarizeEnergyRows, ENERGY_LABEL, SOURCE_LABEL } from "@/lib/reportAgg";
import { BRAND, buildFilename, formatPeriod, formatGeneratedAt, computeKPIs, brandedHtml, excelMetaRows, buildCsv, renderReportPdf, energyKpisFromSummary, poiVisitsKpiCards, topVisitedPois, fmtClock, formatStops, fmtDuration, geoText, geoCellProps } from "@/lib/reportBranding";

// Indice de pré-validation UI (le backend REPORT_MAX_VEHICLES_USER reste l'autorité absolue).
const MAX_VEHICLES_HINT = 200;
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const dl = (blob, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href); };

const NUMERIC = new Set(["distance_km", "moving_min", "idle_min", "stop_min", "max_speed", "speeding_events", "points", "count", "rpm", "fuel_pct", "coolant_temp", "total_mileage_km", "fuel_used_l", "fuel_l", "can_speed", "throttle", "score", "harsh_brakes", "harsh_accels", "cornerings", "km", "dtc_count", "consumption_value", "price", "cost_xpf", "visits", "vehicles", "episodes", "max_excess", "cov_points", "cov_matched", "cov_known_limit", "cov_unknown", "cov_ambiguous", "cov_unsupported"]);
const ALL_ITEMS = REPORT_GROUPS.flatMap((g) => g.items);

const baseColumns = (sel) => {
  let cols = [];
  if (sel.src === "activity") cols = ACTIVITY_COLS[sel.key] || ACTIVITY_COLS.activite;
  else if (sel.src === "road") cols = ROAD_SPEED_COLS;
  else if (sel.src === "poivisits") cols = POI_VISITS_COLS;
  else if (sel.src === "energy") cols = ENERGY_COLS;
  else if (sel.src === "can") cols = [["vehicle", "Véhicule"], ["plate", "Immat."], ["rpm", "Régime (tr/min)"], ["fuel_pct", "Carburant %"], ["coolant_temp", "Temp. °C"], ["total_mileage_km", "Km CAN"], ["can_speed", "V. CAN"], ["dtc_count", "DTC"], ["dtc_codes", "Codes DTC"]];
  else if (sel.src === "eco") cols = [["name", "Conducteur / Véhicule"], ["type_label", "Type"], ["score", "Score /100"], ["harsh_accels", "Accél."], ["harsh_brakes", "Freinages"], ["cornerings", "Virages"], ["speedings", "Excès"], ["idle_min", "Ralenti (min)"], ["km", "Km (filtré)"], ["raw_km", "Km (brut GPS)"]];
  else if (sel.src === "alerts") cols = [["vehicle", "Véhicule"], ["plate", "Immat."], ["count", "Événements"], ["last", "Dernier"]];
  else if (sel.src === "alertsum") cols = [["label", "Type d'alerte"], ["count", "Nombre"]];
  return cols.map((c) => ({ key: c[0], label: c[1], enabled: true, agg: "none", numeric: NUMERIC.has(c[0]) }));
};

const todayStart = () => toLocalInput(accountMidnightUTC(0));
const defaultCrit = () => ({ nom: "", units: [], start: todayStart(), end: "", limit: "", resume: false, format: "WEB" });

const CRIT_FIELDS = [
  { key: "nom", label: "Nom", type: "text" },
  { key: "units", label: "Unité/Conducteur", type: "units" },
  { key: "start", label: "Départ", type: "datetime-local" },
  { key: "end", label: "Clôture", type: "datetime-local" },
  { key: "limit", label: "Limite vitesse", type: "number", suffix: "km/h" },
  { key: "resume", label: "Résumer", type: "checkbox" },
  { key: "format", label: "Format du rapport", type: "select", options: [["WEB", "WEB (écran)"], ["HTML", "HTML"], ["CSV", "CSV"], ["PDF", "PDF"], ["EXCEL", "Excel"]] },
];

const aggVal = (rows, key, agg) => {
  const nums = rows.map((r) => Number(r[key]) || 0);
  if (!nums.length) return "";
  if (agg === "min") return Math.min(...nums);
  if (agg === "max") return Math.max(...nums);
  if (agg === "sum") return Math.round(nums.reduce((a, b) => a + b, 0));
  if (agg === "avg") return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
  if (agg === "count") return nums.length;
  return "";
};

// Six cartes principales (regroupent les rapports existants, aucune perte d'accès) + section « Rapports avancés ».
const CARDS = [
  { key: "activite", title: "Activité", icon: Activity, desc: "Trajets, temps de conduite et résumé quotidien", items: ["activite"] },
  { key: "kilometrage", title: "Kilométrage", icon: Gauge, desc: "Distances par véhicule et par période", items: ["kilometrage"] },
  { key: "arrets", title: "Arrêts et ralenti", icon: Timer, desc: "Durées et lieux des arrêts et du ralenti", items: ["arret", "ralenti"] },
  { key: "conduite", title: "Vitesse et conduite", icon: TrendingUp, desc: "Excès de vitesse et comportement de conduite", items: ["exces", "road_speed", "eco_score"] },
  { key: "energie", title: "Carburant et coûts", icon: Fuel, desc: "Consommation et dépenses (selon équipement)", items: ["energy_cost"] },
  { key: "entretien", title: "Entretien", icon: Wrench, desc: "Échéances et opérations de maintenance", items: ["maintenance"] },
];
const ADVANCED = [
  { group: "Productivité & zones", desc: "Visites de points d'intérêt, événements de zone et synthèse des alertes.", items: ["visites_poi", "evenements_zone", "synthese_alertes"] },
  { group: "Réseau & communication", desc: "Pertes réseau et déconnexions des traceurs.", items: ["reseau"] },
  { group: "Données techniques (CAN)", desc: "Dernières valeurs moteur remontées par le bus CAN.", items: ["can_data"] },
];
const PRESETS = [["today", "Aujourd'hui"], ["yesterday", "Hier"], ["7d", "7 derniers jours"], ["month", "Ce mois"], ["custom", "Dates personnalisées"]];
const toLocalInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
// Décalage (minutes) du fuseau `tz` à l'instant `date` (robuste DST ; aucun offset codé en dur).
const tzOffsetMinutes = (tz, date) => {
  const m = {};
  new Intl.DateTimeFormat("en-US", { timeZone: tz, hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })
    .formatToParts(date).forEach((p) => { m[p.type] = p.value; });
  const asUTC = Date.UTC(m.year, m.month - 1, m.day, m.hour === "24" ? 0 : m.hour, m.minute, m.second);
  return (asUTC - date.getTime()) / 60000;
};
// Instant UTC de 00:00 (fuseau du COMPTE) pour la date calendaire du compte décalée de deltaDays.
// dayOfMonth optionnel : force le jour du mois (pour « Ce mois »).
const accountMidnightUTC = (deltaDays = 0, dayOfMonth = null) => {
  const tz = getUserSettings().timezone || "Pacific/Noumea";
  const m = {};
  new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" })
    .formatToParts(new Date()).forEach((p) => { m[p.type] = p.value; });
  const guess = new Date(Date.UTC(Number(m.year), Number(m.month) - 1, (dayOfMonth || Number(m.day)) + deltaDays, 0, 0, 0));
  return new Date(guess.getTime() - tzOffsetMinutes(tz, guess) * 60000);
};
const presetRange = (p) => {
  const now = new Date();
  if (p === "today") return { start: toLocalInput(accountMidnightUTC(0)), end: toLocalInput(now) };
  if (p === "yesterday") return { start: toLocalInput(accountMidnightUTC(-1)), end: toLocalInput(accountMidnightUTC(0)) };
  if (p === "7d") return { start: toLocalInput(accountMidnightUTC(-6)), end: toLocalInput(now) };
  if (p === "month") return { start: toLocalInput(accountMidnightUTC(0, 1)), end: toLocalInput(now) };
  return null;
};

export default function ReportsPage() {
  const [sel, setSel] = useState(ALL_ITEMS[0]);
  const [values, setValues] = useState(defaultCrit());
  const [columns, setColumns] = useState(baseColumns(ALL_ITEMS[0]));
  const [rows, setRows] = useState([]);
  const [episodes, setEpisodes] = useState([]);   // détail excès de vitesse (rapport exces)
  const [excesView, setExcesView] = useState("episodes");  // A9.2-b : "episodes" | "points" (toutes les mesures > seuil)
  const [points, setPoints] = useState([]);       // A9.2-b : mesures détaillées > seuil (borné WEB)
  const [pointsInfo, setPointsInfo] = useState(null);  // A9.2-b : {truncated, count, total, limit}
  const [useSpecificLimit, setUseSpecificLimit] = useState(false);  // A9.2-b : seuils configurés (défaut) vs limite spécifique
  const [visits, setVisits] = useState([]);        // R5 — détail des visites POI (rapport visites_poi)
  const [poiKpis, setPoiKpis] = useState(null);    // R5 — synthèse KPI backend (source de vérité)
  const [poiUnsupported, setPoiUnsupported] = useState(0);  // R5 — POI non analysés (tronçon / sans géométrie)
  const [pois, setPois] = useState([]);            // R5 — POI scopés RBAC (dérive catégories + sélecteur POI)
  const [fPoi, setFPoi] = useState("");            // R5 — filtre POI (id)
  const [poiQuery, setPoiQuery] = useState("");    // R5 — texte du sélecteur POI recherchable
  const [fCategory, setFCategory] = useState("");  // R5 — filtre catégorie
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const lim = limitDays(user);
  const [units, setUnits] = useState([]);
  const [vehIndex, setVehIndex] = useState({});   // plaque -> nom (pour filtrer les lignes éco par véhicule)
  const [vehCompany, setVehCompany] = useState({}); // plaque -> company_id (résolution du client R2)
  const [companies, setCompanies] = useState([]);  // {id,name,timezone} (scopé côté serveur)
  const [historyKey, setHistoryKey] = useState(0);  // force le rechargement de l'historique après génération
  const [saved, setSaved] = useState([]);
  const [energyKpis, setEnergyKpis] = useState(null);  // R4 : résumé backend (source de vérité KPI énergie)
  // R9 — filtres compacts société / pool / véhicule (envoyés au backend, jamais de longue liste d'IDs)
  const [pools, setPools] = useState([]);
  const [fCompany, setFCompany] = useState("");
  const [fPool, setFPool] = useState("");
  const [fVehicle, setFVehicle] = useState("");
  // UX simplifiée : accueil « cartes » -> rapport sélectionné. Préréglages de période + actions secondaires.
  const [view, setView] = useState("home");       // home | report
  const [activeCard, setActiveCard] = useState(null);
  const [preset, setPreset] = useState("today");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  // Rapport « Excès selon la route » (OSM/ODbL) — statut dataset, détail épisodes, couverture EFFECTIVE, paramètres appliqués.
  const [roadMeta, setRoadMeta] = useState(null);
  const [roadEpisodes, setRoadEpisodes] = useState([]);
  const [roadCoverage, setRoadCoverage] = useState(null);
  const [roadParams, setRoadParams] = useState(null);
  const [roadMargin, setRoadMargin] = useState(0);
  const [roadMinDur, setRoadMinDur] = useState(0);

  useEffect(() => {
    Promise.all([api.get("/vehicles"), api.get("/drivers")]).then(([v, d]) => {
      const u = v.data.map((x) => ({ value: x.plate, label: `${x.name} · ${x.plate}`, kind: "veh" }));
      const idx = {}, comp = {};
      v.data.forEach((x) => { idx[x.plate] = x.name; comp[x.plate] = x.company_id; });
      d.data.forEach((x) => u.push({ value: `drv:${x.name}`, label: `👤 ${x.name}`, kind: "drv" }));
      setUnits(u);
      setVehIndex(idx);
      setVehCompany(comp);
    }).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/pools").then((r) => setPools(r.data)).catch(() => {});
    api.get("/pois").then((r) => setPois(r.data || [])).catch(() => {});  // R5 — POI scopés RBAC
    api.get("/road-speed/status").then((r) => setRoadMeta(r.data)).catch(() => {});  // dataset OSM/ODbL (attribution/date/dispo)
    loadSaved();
  }, []);
  const loadSaved = () => api.get("/saved-defs?kind=report").then((r) => setSaved(r.data)).catch(() => {});

  // R9 — véhicules (plaque->id) pour le filtre véhicule + cascades société/pool.
  const [vehId, setVehId] = useState({});   // plaque -> id canonique
  useEffect(() => {
    api.get("/vehicles").then((r) => { const m = {}; r.data.forEach((x) => { m[x.plate] = x.id; }); setVehId(m); }).catch(() => {});
  }, []);
  const scopedPools = pools.filter((p) => !fCompany || p.company_id === fCompany);
  // R5 — POI et catégories restreints au périmètre RBAC (jamais de fuite hors périmètre) + filtre société courant.
  const scopedPois = pois.filter((p) => (!fCompany || p.company_id === fCompany) && (!fCategory || (p.category || "autre") === fCategory));
  const poiCategories = [...new Set(pois.filter((p) => !fCompany || p.company_id === fCompany).map((p) => p.category || "autre"))].sort();
  const scopedVehicles = units.filter((u) => u.kind === "veh"
    && (!fCompany || vehCompany[u.value] === fCompany)
    && (!fPool || (scopedPools.find((p) => p.id === fPool)?.vehicle_ids || []).includes(vehId[u.value])));
  // Query string compacte : société/pool/véhicule (le backend refait l'intersection avec le scope).
  const scopeQS = () => {
    const q = [];
    if (fCompany) q.push(`company_id=${encodeURIComponent(fCompany)}`);
    if (fPool) q.push(`pool_id=${encodeURIComponent(fPool)}`);
    if (fVehicle) q.push(`vehicle_id=${encodeURIComponent(fVehicle)}`);
    return q.length ? `&${q.join("&")}` : "";
  };
  const onFCompany = (v) => { setFCompany(v); setFPool(""); setFVehicle(""); setFPoi(""); setPoiQuery(""); setFCategory(""); };
  const onFPool = (v) => { setFPool(v); setFVehicle(""); };
  const onFCategory = (v) => { setFCategory(v); setFPoi(""); setPoiQuery(""); };
  // R5 — sélecteur POI recherchable : le texte saisi est résolu vers l'id du POI (dans le périmètre RBAC).
  const onPoiQuery = (v) => {
    setPoiQuery(v);
    const match = scopedPois.find((p) => `${p.name}` === v || `${p.name} · ${p.category || "autre"}` === v);
    setFPoi(match ? match.id : "");
  };

  const pickItem = (item, crit, cols) => {
    setSel(item);
    let c = crit || defaultCrit();
    // Nettoyage : un rapport sans conducteurs ne conserve pas une sélection drv:* (évite un
    // rapport accidentel sur toute la flotte à partir d'une définition enregistrée).
    if (item.src !== "eco" && Array.isArray(c.units) && c.units.some((u) => String(u).startsWith("drv:"))) {
      c = { ...c, units: c.units.filter((u) => !String(u).startsWith("drv:")) };
    }
    setValues(c);
    setColumns(cols && cols.length ? cols : baseColumns(item));
  };

  // --- Navigation cartes / préréglages de période ---
  const itemByKey = (k) => ALL_ITEMS.find((i) => i.key === k) || ALL_ITEMS[0];
  const applyPreset = (p) => {
    setPreset(p);
    const r = presetRange(p);
    if (r) setValues((v) => ({ ...v, start: r.start, end: r.end }));
  };
  const openCard = (card) => {
    const it = itemByKey(card.items[0]);
    const r = presetRange("today");
    setActiveCard(card);
    pickItem(it, { ...defaultCrit(), start: r.start, end: r.end });
    setPreset("today"); setCustomize(false); setExportOpen(false); setPlanOpen(false);
    setView("report");
  };
  const openAdvancedItem = (k) => {
    const it = itemByKey(k);
    const r = presetRange("today");
    setActiveCard({ key: "_adv", title: it.label, items: [k], advanced: true });
    pickItem(it, { ...defaultCrit(), start: r.start, end: r.end });
    setPreset("today"); setCustomize(false); setExportOpen(false); setPlanOpen(false);
    setView("report");
  };
  const backHome = () => { setView("home"); setActiveCard(null); };

  const load = useCallback(async () => {
    if (sel.src === "hw") { setRows([]); return []; }
    setLoading(true);
    try {
      const startD = values.start ? new Date(values.start) : new Date(Date.now() - 24 * 3600000);
      const endD = values.end ? new Date(values.end) : new Date();
      if (isNaN(startD.getTime()) || isNaN(endD.getTime())) {
        toast.error("Dates de période invalides"); setRows([]); return [];
      }
      if (endD <= startD) {
        toast.error("La clôture doit être postérieure au départ"); setRows([]); return [];
      }
      const days = (endD.getTime() - startD.getTime()) / 86400000;
      if (lim && days > lim) {
        toast.error(`Période limitée à ${lim} jours maximum pour ne pas surcharger la plateforme`);
        setRows([]); return [];
      }
      const period = `start=${encodeURIComponent(startD.toISOString())}&end=${encodeURIComponent(endD.toISOString())}`;
      const uv = (values.units || []).filter((u) => sel.src === "eco" || !String(u).startsWith("drv:"));
      const plateSel = uv.filter((u) => !String(u).startsWith("drv:"));
      // Pré-validation informative (le backend reste l'autorité absolue sur les seuils).
      if (plateSel.length > MAX_VEHICLES_HINT) {
        toast.error("Ce rapport est trop volumineux. Réduisez la période ou le nombre de véhicules sélectionnés.");
        setRows([]); return [];
      }
      let result = [];
      if (sel.src === "activity") {
        const kind = sel.key === "exces" ? "exces" : "activite";   // exces -> endpoint épisodes (source unique)
        let url = `/reports/${kind}?${period}${scopeQS()}`;
        // A9.2-b : la limite spécifique (override rapport) ne prime QUE si explicitement choisie ; sinon seuils A9.2-a.
        const sendLimit = kind === "exces" ? (useSpecificLimit && values.limit) : values.limit;
        if (sendLimit) url += `&overspeed=${Number(values.limit)}`;
        if (kind === "exces" && excesView === "points") url += `&details=points`;
        if (plateSel.length) url += `&plates=${encodeURIComponent(plateSel.join(","))}`;  // filtre serveur (jamais un 'drv:')
        const r = await api.get(url);
        result = r.data.rows;
        setEpisodes(kind === "exces" ? (r.data.episodes || []) : []);   // détail backend, jamais recalculé
        if (kind === "exces" && excesView === "points") {
          setPoints(r.data.points || []);
          setPointsInfo({ truncated: !!r.data.points_truncated, count: r.data.points_count || 0,
                          total: r.data.points_total || 0, limit: r.data.points_limit || 0 });
        } else {
          setPoints([]); setPointsInfo(null);
        }
      } else if (sel.src === "road") {
        // Excès selon la limitation routière (OSM/ODbL). cooldown=0 : le rapport ne masque AUCUN épisode
        // (le délai entre notifications ne concerne que les alertes). Marge/durée min pilotent le moteur.
        let url = `/reports/road-speed?${period}${scopeQS()}&margin=${Number(roadMargin) || 0}&min_duration=${Number(roadMinDur) || 0}&cooldown=0`;
        if (plateSel.length) url += `&plates=${encodeURIComponent(plateSel.join(","))}`;
        const r = await api.get(url);
        setRoadMeta(r.data.meta || null);
        if (r.data.available === false) {
          setRoadEpisodes([]); setRoadCoverage(null); setRoadParams(null);
          result = [];
        } else {
          setRoadEpisodes(r.data.episodes || []);
          setRoadCoverage(r.data.coverage || null);
          setRoadParams(r.data.params || null);
          const cov = (c) => ({ cov_points: c?.points || 0, cov_matched: c?.matched || 0, cov_known_limit: c?.known_limit || 0, cov_unknown: c?.unknown || 0, cov_ambiguous: c?.ambiguous || 0, cov_unsupported: c?.unsupported || 0 });
          result = (r.data.rows || []).map((row) => ({ ...row, ...cov(row.coverage) }));
        }
      } else if (sel.src === "poivisits") {
        let url = `/reports/poi-visits?${period}${scopeQS()}`;
        if (fPoi) url += `&poi_id=${encodeURIComponent(fPoi)}`;
        if (fCategory) url += `&category=${encodeURIComponent(fCategory)}`;
        const r = await api.get(url);
        result = (r.data.pois || []).map((p) => ({ ...p, vehicle_id: p.poi_id }));  // clé stable + réutilise le rendu générique
        setVisits(r.data.visits || []);                     // détail des visites (jamais recalculé)
        setPoiKpis(r.data.kpis || null);                    // KPI = synthèse backend
        setPoiUnsupported(r.data.unsupported_count || 0);
      } else if (sel.src === "can") {
        const r = await api.get(`/reports/can?_=1${scopeQS()}`);
        result = r.data.rows;
      } else if (sel.src === "energy") {
        let url = `/reports/energy-cost?${period}${scopeQS()}`;
        if (plateSel.length) url += `&plates=${encodeURIComponent(plateSel.join(","))}`;  // sélection filtrée SERVEUR
        const r = await api.get(url);
        result = (r.data.rows || []).map((r2) => ({ ...r2, vehicle_id: r2.vehicle_id || r2.plate }));
        setEnergyKpis(r.data.kpis || null);            // KPI = résumé backend (jamais recalculé)
      } else if (sel.src === "eco") {
        const r = await api.get(`/analytics/eco-scores?${period}${scopeQS()}`);
        // P3 — réponse = deux classements séparés (véhicule / conducteur). On les concatène avec un
        // libellé Type explicite ; km = distance filtrée de référence, raw_km = distance brute GPS.
        // Compat contrat : accepte l'objet {vehicles, drivers} (nouveau) OU un tableau simple (legacy).
        const d = r.data;
        const tag = (arr, t) => (Array.isArray(arr) ? arr : []).map((x) => ({ ...x, type_label: t, score: x.score == null ? (x.score_reason || "—") : x.score }));
        const vehArr = Array.isArray(d) ? d : (d && d.vehicles) || [];
        const drvArr = Array.isArray(d) ? [] : (d && d.drivers) || [];
        result = [...tag(vehArr, "Véhicule"), ...tag(drvArr, "Conducteur")];
      } else if (sel.src === "alerts") {
        const typesQ = (sel.types || []).join(",");
        let url = `/reports/alerts?${period}${scopeQS()}`;
        if (typesQ) url += `&types=${encodeURIComponent(typesQ)}`;
        if (plateSel.length) url += `&plates=${encodeURIComponent(plateSel.join(","))}`;
        const r = await api.get(url);   // agrégation serveur : période exacte, jamais tronquée
        result = (r.data.rows || []).map((r2, i) => ({ ...r2, vehicle_id: r2.vehicle_id || r2.plate || i, last: r2.last ? fmtTime(r2.last) : "" }));
      } else if (sel.src === "alertsum") {
        let url = `/reports/alert-summary?${period}${scopeQS()}`;
        if (plateSel.length) url += `&plates=${encodeURIComponent(plateSel.join(","))}`;
        const r = await api.get(url);
        result = (r.data.rows || []).map((r2) => ({ type: r2.type, label: alertMeta(r2.type).label, count: r2.count, vehicle_id: r2.type }));
      }
      setRows(result);
      return result;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.code;
      if (code === "report_too_large" || e?.response?.status === 413) {
        toast.error(detail?.message || "Ce rapport est trop volumineux. Réduisez la période ou le nombre de véhicules sélectionnés.");
      } else if (code === "report_busy" || e?.response?.status === 429) {
        toast.error(detail?.message || "Une génération de rapport volumineuse est déjà en cours. Réessayez dans quelques instants.");
      } else if (code === "report_timeout" || e?.response?.status === 504) {
        toast.error(detail?.message || "La génération du rapport a dépassé le temps autorisé. Réduisez la période ou le nombre de véhicules.");
      } else if (e?.response?.status === 400 && typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Erreur lors de la génération du rapport.");
      }
      setRows([]);
      return [];
    } finally { setLoading(false); }
  }, [sel, values.start, values.end, values.limit, values.units, lim, fCompany, fPool, fVehicle, fPoi, fCategory, excesView, useSpecificLimit, roadMargin, roadMinDur]);

  useEffect(() => { load(); }, [load]);

  const enabledCols = columns.filter((c) => c.enabled);
  // Filtres compatibles avec le type de rapport : les conducteurs ne sont proposés QUE pour l'éco-conduite.
  const supportsDrivers = sel.src === "eco";
  const unitsForSel = supportsDrivers ? units : units.filter((u) => u.kind !== "drv");
  const shownRows = filterRowsByUnits(rows, values.units, vehIndex, supportsDrivers);
  const displayRows = values.resume ? (sel.src === "energy" ? summarizeEnergyRows(shownRows) : summarizeRows(shownRows, enabledCols)) : shownRows;
  const hasAgg = !values.resume && enabledCols.some((c) => c.agg && c.agg !== "none");
  const dashIf = (v) => (v === null || v === undefined || v === "" ? "—" : v);   // null/undefined -> — (jamais 0)
  // B2 — fuseau société + période multi-jours pour l'affichage des heures géo (jamais d'UTC brut).
  const scope = resolveScope();
  const multiDayReport = (() => {
    const s = values.start ? new Date(values.start) : new Date(Date.now() - 24 * 3600000);
    const e = values.end ? new Date(values.end) : new Date();
    return (e.getTime() - s.getTime()) > 86400000;
  })();
  const GEO_TIME = new Set(["first_departure_at", "last_arrival_at"]);
  const GEO_LOC = new Set(["first_departure_location", "last_arrival_location"]);
  const energyCell = (r, key) => {
    if (key === "energy_type") return ENERGY_LABEL[r.energy_type] || dashIf(r.energy_type);
    if (key === "source") return SOURCE_LABEL[r.source] || dashIf(r.source);
    if (key === "cost_xpf") return (r.cost_xpf === null || r.cost_xpf === undefined) ? "—" : `${r.cost_xpf} F`;
    if (key === "consumption_value" || key === "price") return (r[key] === null || r[key] === undefined) ? "—" : r[key];
    return dashIf(r[key]);
  };
  // Valeur texte d'une cellule (WEB + exports). Heures géo au fuseau société ; lieux -> "—" si absent.
  const POI_DUR = new Set(["presence_s", "avg_presence_s", "stopped_s", "idle_s"]);
  // P2 — absence vs zéro : une ligne d'activité sans AUCUN point GPS sur la période n'a pas de mesure
  // déterminable -> on affiche « — » (jamais 0) pour les indicateurs dérivés. Le vrai 0 (points > 0) est conservé.
  const NO_DATA_DERIVED = new Set(["distance_km", "moving_min", "idle_min", "stop_min", "max_speed", "speeding_events"]);
  const rowNoData = (r) => r && r.points === 0;
  const cell = (r, c) => {
    if (rowNoData(r) && NO_DATA_DERIVED.has(c.key)) return "—";
    if (sel.src === "energy") return energyCell(r, c.key);
    if (sel.src === "poivisits") {
      if (POI_DUR.has(c.key)) return fmtDuration(r[c.key]);
      if (c.key === "freq_per_day") return `${r.freq_per_day} /j`;
      return dashIf(r[c.key]);
    }
    if (c.key === "max_speed") return `${r.max_speed} ${r.speed_unit || "km/h"}`;
    if (GEO_TIME.has(c.key)) return geoText(fmtClock(r[c.key], scope.tz, multiDayReport));
    if (GEO_LOC.has(c.key)) return geoText(r[c.key]);
    return r[c.key];
  };
  // Rendu WEB d'une cellule : les lieux sont tronqués visuellement avec tooltip (valeur complète).
  const renderCell = (r, c) => {
    if (GEO_LOC.has(c.key)) {
      const p = geoCellProps(r[c.key]);
      return <span className={p.className} title={p.title} data-testid={`geo-loc-${c.key}`}>{p.text}</span>;
    }
    return cell(r, c);
  };

  // B3 — Rapport « Excès de vitesse » : détail des épisodes (présentation seule, aucun recalcul).
  const isExces = sel.key === "exces";
  // Items 3/4 : couleur par tranche de dépassement du SEUIL CONFIGURÉ (jamais une limite légale).
  const TIER_COLOR = (tier) => ["#64748B", "#CA8A04", "#EA580C", "#DC2626", "#991B1B"][Math.min(Math.max(tier || 0, 0), 4)];
  const EXCES_WEB_HEADER = ["Véhicule", "Date / heure", "Durée", "Vitesse max", "Seuil", "Dépassement", "Tranche", "Lieu"];
  const EXCES_EXPORT_HEADER = ["Heure", "Véhicule", "Durée", "Vitesse max", "Limite", "Dépassement", "Tranche", "Vitesse absolue > 140", "Lieu"];
  const excesWebRow = (ep) => [
    `${ep.vehicle} (${ep.plate})`, fmtClock(ep.start_at, scope.tz, multiDayReport), fmtDuration(ep.duration_s),
    `${ep.max_speed_kmh} km/h`, `${ep.speed_limit_kmh} km/h`, `+${ep.max_excess_kmh} km/h`, ep.tier_label || "—", ep.location || "—",
  ];
  const excesExportRow = (ep) => [
    fmtClock(ep.start_at, scope.tz, multiDayReport), `${ep.vehicle} (${ep.plate})`, fmtDuration(ep.duration_s),
    `${ep.max_speed_kmh} km/h`, `${ep.speed_limit_kmh} km/h`, `${ep.max_excess_kmh} km/h`, ep.tier_label || "—",
    ep.abs_over_140 ? "oui" : "", ep.location || "—",
  ];
  // A9.2-b — « Toutes les mesures supérieures au seuil » (une ligne par position GPS reçue > seuil).
  const excesShowPoints = isExces && excesView === "points";
  const POINTS_WEB_HEADER = ["Date / heure", "Véhicule", "Immat.", "Conducteur", "Limite", "Vitesse", "Dépassement", "Coordonnées", "Tranche"];
  const fmtClockSec = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const opt = { timeZone: scope.tz || "Pacific/Noumea", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false };
    if (multiDayReport) { opt.day = "2-digit"; opt.month = "2-digit"; }
    return d.toLocaleString("fr-FR", opt);
  };
  const pointCoords = (p) => (p.lat != null && p.lon != null) ? `${Number(p.lat).toFixed(5)}, ${Number(p.lon).toFixed(5)}` : "—";
  const pointWebCells = (p) => [
    fmtClockSec(p.timestamp), p.vehicle || "—", p.plate || "—", p.driver_name || "—",
    `${p.speed_limit_kmh} km/h`, p.speed_kmh, p.excess_kmh, pointCoords(p), p.tier_label || "—",
  ];
  const POINTS_EXPORT_HEADER = ["Date/heure", "Véhicule", "Immatriculation", "Conducteur", "Limite km/h", "Vitesse km/h", "Dépassement km/h", "Tranche", "Vitesse absolue > 140", "Latitude", "Longitude"];
  const pointExportRow = (p) => [fmtClockSec(p.timestamp), p.vehicle || "", p.plate || "", p.driver_name || "", p.speed_limit_kmh, p.speed_kmh, p.excess_kmh, p.tier_label || "—", p.abs_over_140 ? "oui" : "", p.lat, p.lon];

  // R5 — Rapport « Visites POI » : détail des visites (présentation seule, aucun recalcul).
  const isPoiVisits = sel.src === "poivisits";
  const POI_WEB_HEADER = ["Date", "Véhicule", "POI", "Catégorie", "Entrée", "Sortie", "Présence", "Arrêt", "Ralenti", "Type"];
  const fmtDay = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-FR", { timeZone: scope.tz || "Pacific/Noumea", day: "2-digit", month: "2-digit", year: "numeric" });
  };
  const visitRow = (v) => [
    fmtDay(v.entry_at), `${v.vehicle} (${v.plate})`, v.poi, v.category,
    fmtClock(v.entry_at, scope.tz, true), fmtClock(v.exit_at, scope.tz, true),
    fmtDuration(v.presence_s), fmtDuration(v.stopped_s), fmtDuration(v.idle_s), v.type,
  ];
  // R5 — « Sites les plus visités » : présentation dérivée de la synthèse (masqué si POI unique sélectionné / zéro visite).
  const poiTopDetail = (r) => `${r.visits} visite${r.visits > 1 ? "s" : ""} · ${r.vehicles} véh. · ${fmtDuration(r.avg_presence_s)}/visite · ${r.freq_per_day} /j`;
  const topPois = (isPoiVisits && !fPoi) ? topVisitedPois(rows, 5) : [];

  // Rapport « Excès selon la route » (OSM/ODbL) — présentation seule, aucune limite inventée.
  const isRoad = sel.src === "road";
  const ROAD_WEB_HEADER = ["Véhicule", "Date / heure", "Durée", "Route / tronçon", "Limite", "V. max", "Dépassement", "Source / qualité", "Lieu"];
  const roadQuality = (e) => {
    const src = String(e.source || "osm").toUpperCase();
    const q = e.quality && String(e.quality).toLowerCase() !== String(e.source || "").toLowerCase() ? e.quality : null;
    return [src, q].filter(Boolean).join(" · ");
  };
  const roadLoc = (e) => (e.lat != null && e.lon != null) ? `${Number(e.lat).toFixed(5)}, ${Number(e.lon).toFixed(5)}` : (e.road || "—");
  const roadWebRow = (e) => [
    `${e.vehicle || "—"}${e.plate ? ` (${e.plate})` : ""}`,
    fmtClock(e.start, scope.tz, multiDayReport), fmtDuration(e.duration_s),
    e.road || e.ref || "—", `${e.limit} km/h`, `${e.max_speed} km/h`, `+${e.excess} km/h`,
    roadQuality(e), roadLoc(e),
  ];

  // R2 — résout le CLIENT et le fuseau depuis le SCOPE réellement utilisé (jamais une société supposée).
  function resolveScope() {
    const byId = {}; companies.forEach((c) => { byId[c.id] = c; });
    const uv = (values.units || []).filter((u) => sel.src === "eco" || !String(u).startsWith("drv:"));
    const plateSel = uv.filter((u) => !String(u).startsWith("drv:"));
    const cids = new Set();
    if (plateSel.length) plateSel.forEach((p) => { const c = vehCompany[p]; if (c) cids.add(c); });
    else if (user?.company_id) cids.add(user.company_id);
    if (cids.size === 1) { const c = byId[[...cids][0]]; return { client: c?.name || "—", tz: c?.timezone || "Pacific/Noumea", cid: [...cids][0] }; }
    if (user?.role !== "admin" && user?.company_id) { const c = byId[user.company_id]; return { client: c?.name || "—", tz: c?.timezone || "Pacific/Noumea", cid: user.company_id }; }
    return { client: "Toutes les sociétés", tz: "Pacific/Noumea", cid: null };
  };

  const runExport = async (fmt, rowsToExport) => {
    const isActivity = sel.src === "activity";
    const titre = values.nom || sel.label;
    const startD = values.start ? new Date(values.start) : new Date(Date.now() - 24 * 3600000);
    const endD = values.end ? new Date(values.end) : new Date();
    const { client, tz } = resolveScope();
    const multiDay = (endD.getTime() - startD.getTime()) > 86400000;
    const period = formatPeriod(startD, endD, tz);
    const generatedAt = formatGeneratedAt(tz);
    const kpis = sel.src === "poivisits" ? poiVisitsKpiCards(poiKpis)
      : sel.src === "energy" ? energyKpisFromSummary(energyKpis) : computeKPIs(sel.src, rowsToExport);
    const fname = (ext) => buildFilename({ client, report: titre, start: startD, end: endD, ext, tz });

    // A9.2-b — Mode « Toutes les mesures » : exports dédiés (CSV serveur streaming / Excel borné / PDF synthèse).
    if (excesShowPoints) {
      if (fmt === "CSV") {
        const per = `start=${encodeURIComponent(startD.toISOString())}&end=${encodeURIComponent(endD.toISOString())}`;
        let url = `/reports/exces/export.csv?${per}${scopeQS()}`;
        if (useSpecificLimit && values.limit) url += `&overspeed=${Number(values.limit)}`;
        const uv = (values.units || []).filter((u) => !String(u).startsWith("drv:"));
        if (uv.length) url += `&plates=${encodeURIComponent(uv.join(","))}`;
        try {
          const resp = await api.get(url, { responseType: "blob" });
          dl(new Blob([resp.data], { type: "text/csv;charset=utf-8;" }), fname("csv"));
          toast.success("Export CSV généré");
        } catch { toast.error("Erreur lors de l'export CSV"); }
        return;
      }
      if (fmt === "EXCEL") {
        if (pointsInfo?.truncated) { toast.error("Ce rapport contient trop de mesures pour Excel. Utilisez l'export CSV."); return; }
        const meta = excelMetaRows({ title: titre, client, period, generatedAt });
        const xH = POINTS_EXPORT_HEADER.map((h) => csvSafe(h));
        const xB = points.map((p) => pointExportRow(p).map((v, i) => (i >= 4 ? v : csvSafe(v))));
        const ws = XLSX.utils.aoa_to_sheet([...meta, xH, ...xB]);
        ws["!cols"] = xH.map((h, i) => ({ wch: Math.min(40, Math.max(String(h).length + 2, 10)) }));
        const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Mesures");
        XLSX.writeFile(wb, fname("xlsx"));
        toast.success("Export Excel généré");
        return;
      }
      if (fmt === "PDF") {
        toast.info("L'export PDF utilise la synthèse des épisodes. Pour toutes les mesures, utilisez CSV ou Excel.");
        // PDF poursuit avec la synthèse des épisodes (bornée) ci-dessous.
      }
    }

    // Rapport « Excès selon la route » : CSV serveur (une ligne par épisode, heures au fuseau société,
    // paramètres appliqués en préambule). cooldown=0 : aucun épisode masqué.
    if (sel.src === "road" && fmt === "CSV") {
      const per = `start=${encodeURIComponent(startD.toISOString())}&end=${encodeURIComponent(endD.toISOString())}`;
      let url = `/reports/road-speed/export.csv?${per}${scopeQS()}&margin=${Number(roadMargin) || 0}&min_duration=${Number(roadMinDur) || 0}&cooldown=0`;
      const uvr = (values.units || []).filter((u) => !String(u).startsWith("drv:"));
      if (uvr.length) url += `&plates=${encodeURIComponent(uvr.join(","))}`;
      try {
        const resp = await api.get(url, { responseType: "blob" });
        dl(new Blob([resp.data], { type: "text/csv;charset=utf-8;" }), fname("csv"));
        toast.success("Export CSV généré");
      } catch { toast.error("Erreur lors de l'export CSV"); }
      return;
    }

    // Colonnes du tableau (identiques WEB) — les 4 colonnes géo cochables en font partie.
    const baseHeader = enabledCols.map((c) => c.label);
    const baseBody = rowsToExport.map((r) => enabledCols.map((c) => cell(r, c)));
    const numericIdx = enabledCols.map((c, i) => (c.numeric ? i : -1)).filter((i) => i >= 0);

    // « Arrêts principaux » : colonne présente UNIQUEMENT dans les exports détaillés (CSV/HTML/Excel),
    // jamais dans le tableau WEB ni dans le tableau PDF (rendu en section dédiée côté PDF).
    const detailStops = isActivity && !isExces && ["CSV", "HTML", "EXCEL"].includes(fmt);
    const header = detailStops ? [...baseHeader, "Arrêts principaux"] : baseHeader;
    const body = detailStops
      ? baseBody.map((row, i) => [...row, formatStops(rowsToExport[i].main_stops, tz, multiDay)])
      : baseBody;

    // B3 — Rapport « Excès de vitesse » : détail des épisodes (une ligne par épisode) pour les exports.
    const excesRows = isExces ? episodes.map(excesExportRow) : [];
    const excesSafe = excesRows.map((row) => row.map((v) => csvSafe(v)));   // anti-formule sur toutes les cellules
    // R5 — Rapport « Visites POI » : détail des visites (une ligne par visite) pour les exports.
    const poiRows = isPoiVisits ? visits.map(visitRow) : [];
    const poiSafe = poiRows.map((row) => row.map((v) => csvSafe(v)));
    const POI_NUM_IDX = [6, 7, 8];   // Présence / Arrêt / Ralenti (durées) alignées à droite dans HTML/PDF

    // R1C : neutralisation formule CSV/Excel (cellules texte uniquement ; nombres intacts).
    const numSet = new Set(numericIdx);
    const safeHeader = header.map((h) => csvSafe(h));
    const safeBody = body.map((row) => row.map((v, i) => (numSet.has(i) ? v : csvSafe(v))));

    if (fmt === "CSV") {
      // B3 : pour l'excès, l'export CSV = une ligne par ÉPISODE (détail). R5 : une ligne par VISITE.
      const csvH = isExces ? EXCES_EXPORT_HEADER.map((h) => csvSafe(h)) : isPoiVisits ? POI_WEB_HEADER.map((h) => csvSafe(h)) : safeHeader;
      const csvB = isExces ? excesSafe : isPoiVisits ? poiSafe : safeBody;
      dl(new Blob([buildCsv({ header: csvH, body: csvB })], { type: "text/csv;charset=utf-8;" }), fname("csv"));
      toast.success("Export CSV généré");
    } else if (fmt === "HTML") {
      const extraTable = isExces
        ? { title: "Détail des excès de vitesse", header: EXCES_EXPORT_HEADER, body: excesRows, numericIdx: [3, 4, 5] }
        : isPoiVisits
        ? { title: "Détail des visites", header: POI_WEB_HEADER, body: poiRows, numericIdx: POI_NUM_IDX }
        : undefined;
      const html = brandedHtml({ title: titre, client, period, generatedAt, kpis, header: baseHeader, body: baseBody, numericIdx, extraTable });
      dl(new Blob([html], { type: "text/html;charset=utf-8;" }), fname("html"));
      toast.success("Export HTML généré");
    } else if (fmt === "EXCEL") {
      const meta = excelMetaRows({ title: titre, client, period, generatedAt });
      const xH = isExces ? EXCES_EXPORT_HEADER.map((h) => csvSafe(h)) : isPoiVisits ? POI_WEB_HEADER.map((h) => csvSafe(h)) : safeHeader;
      const xB = isExces ? excesSafe : isPoiVisits ? poiSafe : safeBody;
      const ws = XLSX.utils.aoa_to_sheet([...meta, xH, ...xB]);
      ws["!cols"] = xH.map((h, i) => ({ wch: Math.min(40, Math.max(String(h).length + 2, ...xB.map((r) => String(r[i] ?? "").length + 2), 10)) }));
      ws["!freeze"] = { xSplit: 0, ySplit: meta.length + 1 };
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Rapport");
      XLSX.writeFile(wb, fname("xlsx"));
      toast.success("Export Excel généré");
    } else {
      try {
        // PDF : arrêts principaux en section compacte, UNIQUEMENT pour 1..5 véhicules (>5 = surcharge évitée).
        const stopsSection = (isActivity && !isExces && rowsToExport.length <= 5)
          ? rowsToExport.map((r) => ({ vehicle: r.vehicle, plate: r.plate, text: formatStops(r.main_stops, tz, multiDay) })).filter((x) => x.text)
          : null;
        // PDF excès : Synthèse (tableau principal) puis Détail des épisodes. R5 : Synthèse POI puis Détail des visites.
        const detailTable = isExces
          ? { title: "Détail des excès de vitesse", header: EXCES_EXPORT_HEADER, body: excesRows, numericIdx: [3, 4, 5] }
          : isPoiVisits
          ? { title: "Détail des visites", header: POI_WEB_HEADER, body: poiRows, numericIdx: POI_NUM_IDX }
          : undefined;
        const genBy = (user?.name || "").trim() ? `Généré par : ${user.name.trim()}` : undefined;   // R9 — identité de session
        // R5 — « Sites les plus visités » dans le PDF (compact, page 1, dérivé de la synthèse ; masqué si POI unique).
        const topBlock = (isPoiVisits && !fPoi) ? topVisitedPois(rowsToExport, 5).map((r) => ({ poi: r.poi, detail: poiTopDetail(r) })) : undefined;
        const doc = await renderReportPdf({ jsPDF, autoTable, title: titre, client, period, generatedAt, generatedBy: genBy, kpis,
          header: baseHeader, body: baseBody, numericIdx, stopsSection, stopsTitle: "Arrêts principaux", detailTable,
          tableTitle: isPoiVisits ? "Synthèse par POI" : undefined, topBlock });
        doc.save(fname("pdf"));
        toast.success("Export PDF généré");
      } catch (e) { toast.error("Erreur lors de la génération du PDF"); }
    }
  };

  const onOK = async (fmtOverride) => {
    const fmt = fmtOverride || values.format;
    const data = await load();               // attend la fin du chargement : l'export utilise CES données
    if (sel.src === "hw" || data == null) return;
    if (["CSV", "PDF", "HTML", "EXCEL"].includes(fmt)) {
      const filtered = filterRowsByUnits(data || [], values.units, vehIndex, sel.src === "eco");
      // P2 — exports : une ligne sans point GPS sur la période -> indicateurs dérivés vidés (cellule absente, jamais 0).
      const gated = filtered.map((r) => (r && r.points === 0)
        ? { ...r, distance_km: "", moving_min: "", idle_min: "", stop_min: "", max_speed: "", speeding_events: "" } : r);
      await runExport(fmt, values.resume ? summarizeRows(filtered, enabledCols) : gated);  // resume reste sur les valeurs numériques
    }
    // R3 : historiser la génération RÉELLEMENT demandée (bouton OK), jamais un refresh de preview.
    try {
      const startD = values.start ? new Date(values.start) : new Date(Date.now() - 24 * 3600000);
      const endD = values.end ? new Date(values.end) : new Date();
      const { cid } = resolveScope();
      const uv = (values.units || []).filter((u) => sel.src === "eco" || !String(u).startsWith("drv:"));
      const plateSel = uv.filter((u) => !String(u).startsWith("drv:"));
      await api.post("/report-runs", {
        report_name: values.nom || sel.label, base_key: sel.src, format: fmt,
        company_id: cid, period_start: startD.toISOString(), period_end: endD.toISOString(),
        vehicle_count: plateSel.length || (data || []).length, row_count: (data || []).length, status: "generated",
      });
      setHistoryKey((k) => k + 1);
    } catch { /* l'historique ne doit pas bloquer la génération */ }
  };

  const onAdd = async () => {
    const name = window.prompt("Nom du nouveau rapport :", values.nom || sel.label);
    if (!name) return;
    try { await api.post("/saved-defs", { kind: "report", name, base_key: sel.key, criteria: values, columns }); toast.success("Rapport enregistré"); loadSaved(); }
    catch { toast.error("Erreur"); }
  };
  const delSaved = async (id) => { await api.delete(`/saved-defs/${id}`); loadSaved(); };

  const isHw = sel.src === "hw";

  return (
    <Layout>
      <div className="mb-5">
        <p className="label-mono">Analyse</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-3"><FileBarChart className="w-7 h-7 brand-teal" /> Rapports</h1>
      </div>

      {view === "home" ? (
        <div data-testid="report-home">
          {saved.length > 0 && (
            <div className="mb-6" data-testid="report-saved-home">
              <p className="label-mono mb-2">Mes rapports enregistrés</p>
              <div className="flex flex-wrap gap-2">
                {saved.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1 border border-border rounded-full pl-3 pr-1 py-1 bg-card">
                    <button onClick={() => { const base = ALL_ITEMS.find((i) => i.key === s.base_key) || ALL_ITEMS[0]; setActiveCard({ key: "_saved", title: s.name, items: [base.key] }); pickItem(base, s.criteria, s.columns); setPreset(s.criteria?.start ? "custom" : "today"); setView("report"); }}
                      data-testid={`saved-report-${s.id}`} className="text-[13px] text-[#2ec5db] hover:underline">{s.name}</button>
                    <button onClick={() => delSaved(s.id)} data-testid={`saved-report-del-${s.id}`} className="text-[#EF4444] p-0.5 hover:bg-[#EF4444]/10 rounded-full"><Trash2 className="w-3.5 h-3.5" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="report-cards">
            {CARDS.map((card) => (
              <button key={card.key} onClick={() => openCard(card)} data-testid={`report-card-${card.key}`}
                className="text-left border border-border rounded-xl bg-card p-5 hover:border-[#17A2B8] hover:shadow-md transition-all flex gap-4 items-start">
                <span className="shrink-0 w-12 h-12 rounded-lg bg-[#17A2B8]/15 flex items-center justify-center"><card.icon className="w-6 h-6 text-[#17A2B8]" /></span>
                <span className="min-w-0"><span className="font-bold text-base block">{card.title}</span><span className="text-sm text-muted-foreground">{card.desc}</span></span>
              </button>
            ))}
          </div>

          <div className="mt-6 border border-border rounded-xl bg-card overflow-hidden" data-testid="report-advanced">
            <button onClick={() => setAdvancedOpen((o) => !o)} data-testid="report-advanced-toggle"
              className="w-full flex items-center justify-between px-5 py-3.5 text-left">
              <span className="font-bold flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 brand-orange" /> Rapports avancés</span>
              {advancedOpen ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronRight className="w-5 h-5 text-muted-foreground" />}
            </button>
            {advancedOpen && (
              <div className="px-5 pb-5 space-y-4 border-t border-border pt-4" data-testid="report-advanced-body">
                {ADVANCED.map((grp) => (
                  <div key={grp.group}>
                    <p className="font-semibold text-sm">{grp.group}</p>
                    <p className="text-xs text-muted-foreground mb-2">{grp.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {grp.items.map((k) => { const it = itemByKey(k); return (
                        <button key={k} onClick={() => openAdvancedItem(k)} data-testid={`report-adv-${k}`}
                          className="text-[13px] border border-border rounded-md px-3 py-1.5 hover:border-[#17A2B8] hover:text-[#17A2B8] flex items-center gap-1.5">
                          {it.src === "hw" && <Cpu className="w-3 h-3 opacity-60" />}{it.label}
                        </button>
                      ); })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Rapports planifiés — accès DIRECT depuis l'accueil (jamais caché dans « Rapports avancés ») */}
          <div className="mt-4 border border-border rounded-xl bg-card overflow-hidden" data-testid="report-scheduled">
            <button onClick={() => setScheduledOpen((o) => !o)} data-testid="report-scheduled-toggle"
              className="w-full flex items-center justify-between px-5 py-3.5 text-left">
              <span className="font-bold flex items-center gap-2"><CalendarClock className="w-4 h-4 brand-teal" /> Rapports planifiés
                <span className="text-xs font-normal text-muted-foreground">— envois automatiques par email</span></span>
              {scheduledOpen ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronRight className="w-5 h-5 text-muted-foreground" />}
            </button>
            {scheduledOpen && (
              <div className="px-5 pb-5 border-t border-border pt-4" data-testid="report-scheduled-body">
                <ScheduledReportsPanel />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div data-testid="report-view">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <button onClick={backHome} data-testid="report-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-[#17A2B8] font-semibold"><ChevronLeft className="w-4 h-4" /> Tous les rapports</button>
            <h2 className="font-bold text-lg">{activeCard?.title || sel.label}</h2>
          </div>
          {activeCard && activeCard.items.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="report-variants">
              {activeCard.items.map((k) => { const it = itemByKey(k); return (
                <button key={k} onClick={() => pickItem(it, { ...values, nom: "" })} data-testid={`report-variant-${k}`}
                  className={`text-[13px] px-3 py-1.5 rounded-full border ${sel.key === k ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground hover:border-[#17A2B8]"}`}>{it.label}</button>
              ); })}
            </div>
          )}

          {!isHw && (
            <div className="border border-border rounded-xl bg-card p-4 mb-4 space-y-3" data-testid="report-form">
              <div className="flex flex-wrap items-center gap-2" data-testid="report-scope-filters">
                <span className="label-mono text-muted-foreground">Périmètre</span>
                <select data-testid="filter-company" value={fCompany} onChange={(e) => onFCompany(e.target.value)} className="text-[12px] rounded-md border border-border bg-card px-2 py-1.5">
                  <option value="">Toutes les sociétés</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select data-testid="filter-pool" value={fPool} onChange={(e) => onFPool(e.target.value)} className="text-[12px] rounded-md border border-border bg-card px-2 py-1.5">
                  <option value="">Tous les groupes</option>
                  {scopedPools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select data-testid="filter-vehicle" value={fVehicle} onChange={(e) => setFVehicle(e.target.value)} className="text-[12px] rounded-md border border-border bg-card px-2 py-1.5">
                  <option value="">Tous les véhicules</option>
                  {scopedVehicles.map((u) => <option key={u.value} value={vehId[u.value] || ""}>{u.label}</option>)}
                </select>
                {isPoiVisits && (
                  <>
                    <select data-testid="filter-poi-category" value={fCategory} onChange={(e) => onFCategory(e.target.value)} className="text-[12px] rounded-md border border-border bg-card px-2 py-1.5">
                      <option value="">Toutes les catégories</option>
                      {poiCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input data-testid="filter-poi" list="poi-options" value={poiQuery} onChange={(e) => onPoiQuery(e.target.value)} placeholder="Rechercher un POI…" className="text-[12px] rounded-md border border-border bg-card px-2 py-1.5 min-w-[180px]" />
                    <datalist id="poi-options">{scopedPois.map((p) => <option key={p.id} value={p.name}>{p.category || "autre"}</option>)}</datalist>
                  </>
                )}
              </div>

              {sel.period === "snapshot" ? (
                <p className="text-[12px] text-muted-foreground italic" data-testid="period-hint">Instantané — dernière donnée connue (pas de période).</p>
              ) : sel.period === "monthly" ? (
                <p className="text-[12px] text-muted-foreground italic" data-testid="period-hint">Période : mois en cours.</p>
              ) : (
                <div className="flex flex-wrap items-center gap-2" data-testid="report-period">
                  <span className="label-mono text-muted-foreground">Période</span>
                  {PRESETS.map(([p, lbl]) => (
                    <button key={p} onClick={() => applyPreset(p)} data-testid={`period-${p}`}
                      className={`text-[12px] px-3 py-1.5 rounded-full border ${preset === p ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border text-muted-foreground hover:border-[#17A2B8]"}`}>{lbl}</button>
                  ))}
                  {preset === "custom" && (
                    <span className="flex items-center gap-2">
                      <input type="datetime-local" value={values.start || ""} onChange={(e) => setValues((v) => ({ ...v, start: e.target.value }))} data-testid="period-custom-start" className="text-[12px] rounded-md border border-border bg-card px-2 py-1.5" />
                      <span className="text-xs">→</span>
                      <input type="datetime-local" value={values.end || ""} onChange={(e) => setValues((v) => ({ ...v, end: e.target.value }))} data-testid="period-custom-end" className="text-[12px] rounded-md border border-border bg-card px-2 py-1.5" />
                    </span>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button onClick={() => onOK("WEB")} data-testid="report-show" className="bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold px-5 py-2 rounded-md text-sm flex items-center gap-1.5"><FileBarChart className="w-4 h-4" /> Afficher le rapport</button>
                <div className="relative">
                  <button onClick={() => setExportOpen((o) => !o)} data-testid="report-export-btn" className="border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] text-muted-foreground font-semibold px-4 py-2 rounded-md text-sm flex items-center gap-1.5"><Download className="w-4 h-4" /> Exporter <ChevronDown className="w-3.5 h-3.5" /></button>
                  {exportOpen && (
                    <div className="absolute z-20 mt-1 w-40 bg-card border border-border rounded-md shadow-lg py-1" data-testid="report-export-menu">
                      {[["PDF", "PDF"], ["EXCEL", "Excel"], ["CSV", "CSV"], ["HTML", "HTML"]].map(([f, lbl]) => (
                        <button key={f} onClick={() => { setExportOpen(false); onOK(f); }} data-testid={`report-export-${f}`} className="w-full text-left px-3 py-1.5 text-sm hover:bg-black/5">{lbl}</button>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => setCustomize((o) => !o)} data-testid="report-customize-btn" className="border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] text-muted-foreground font-semibold px-4 py-2 rounded-md text-sm flex items-center gap-1.5"><SlidersHorizontal className="w-4 h-4" /> Personnaliser</button>
                <button onClick={() => setPlanOpen((o) => !o)} data-testid="report-plan-btn" className="text-[#17A2B8] hover:underline font-semibold px-2 py-2 text-sm flex items-center gap-1.5"><CalendarClock className="w-4 h-4" /> Planifier l'envoi</button>
              </div>
            </div>
          )}

          {customize && (
            <div className="mb-4" data-testid="report-customize-panel">
              <ConfigPanel title={values.nom || sel.label} criteriaFields={CRIT_FIELDS} values={values} setValues={setValues}
                columns={columns} setColumns={setColumns} units={unitsForSel} onOK={onOK} onAdd={onAdd} addLabel="Enregistrer ce rapport"
                extraContent={{ 1: <ReportHistoryPanel key={historyKey} />, 2: <ScheduledReportsPanel /> }} />
            </div>
          )}
          {planOpen && (
            <div className="mb-4 border border-border rounded-xl bg-card p-4" data-testid="report-plan-panel">
              <ScheduledReportsPanel />
            </div>
          )}

          {isExces && !isHw && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-3 px-3 py-2.5 border border-border rounded-lg bg-card" data-testid="exces-controls">
              <div className="flex items-center gap-2">
                <span className="label-mono text-muted-foreground">Vue</span>
                <label className="flex items-center gap-1.5 text-[13px] cursor-pointer" data-testid="exces-view-episodes">
                  <input type="radio" name="exces-view" checked={excesView === "episodes"} onChange={() => setExcesView("episodes")} />
                  Épisodes d'excès
                </label>
                <label className="flex items-center gap-1.5 text-[13px] cursor-pointer" data-testid="exces-view-points">
                  <input type="radio" name="exces-view" checked={excesView === "points"} onChange={() => setExcesView("points")} />
                  Toutes les mesures supérieures au seuil
                </label>
              </div>
              <div className="flex items-center gap-2">
                <span className="label-mono text-muted-foreground">Limite vitesse</span>
                <label className="flex items-center gap-1.5 text-[13px] cursor-pointer" data-testid="exces-limit-configured">
                  <input type="radio" name="exces-limit" checked={!useSpecificLimit} onChange={() => setUseSpecificLimit(false)} />
                  Utiliser les seuils configurés
                </label>
                <label className="flex items-center gap-1.5 text-[13px] cursor-pointer" data-testid="exces-limit-specific">
                  <input type="radio" name="exces-limit" checked={useSpecificLimit} onChange={() => setUseSpecificLimit(true)} />
                  Utiliser une limite spécifique
                </label>
                {useSpecificLimit && (
                  <span className="flex items-center gap-1">
                    <input type="number" min="10" max="300" value={values.limit || ""} data-testid="exces-limit-input"
                      onChange={(e) => setValues((v) => ({ ...v, limit: e.target.value }))}
                      className="w-20 text-[13px] rounded-md border border-border bg-card px-2 py-1" />
                    <span className="text-[12px] text-muted-foreground">km/h</span>
                  </span>
                )}
              </div>
            </div>
          )}

          {isExces && !isHw && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-3 px-3 py-2 text-[11px] text-muted-foreground border border-border/60 rounded-lg bg-secondary/20" data-testid="exces-legend">
              <span className="font-semibold text-foreground">Tranches de dépassement du seuil configuré du véhicule</span>
              {[1, 2, 3, 4].map((t) => (
                <span key={t} className="inline-flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full" style={{ background: TIER_COLOR(t) }} />
                  {t === 1 ? "+1 à +20" : t === 2 ? "+20 à +30" : t === 3 ? "+30 à +40" : "+40 et plus"}
                </span>
              ))}
              <span className="inline-flex items-center gap-1">
                <span className="w-3 h-3 rounded" style={{ background: "#7C3AED" }} />
                Vitesse absolue &gt; 140 km/h
              </span>
              <span className="italic">Ces seuils sont la configuration du véhicule, jamais une limitation routière.</span>
            </div>
          )}

          {isRoad && !isHw && (
            <div className="mb-3 space-y-3" data-testid="road-panel">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-3 py-2.5 border border-border rounded-lg bg-card" data-testid="road-controls">
                <div className="flex items-center gap-2">
                  <span className="label-mono text-muted-foreground">Marge de dépassement</span>
                  <input type="number" min="0" max="50" value={roadMargin} data-testid="road-margin-input"
                    onChange={(e) => setRoadMargin(Math.max(0, Math.min(50, Number(e.target.value) || 0)))}
                    className="w-20 text-[13px] rounded-md border border-border bg-card px-2 py-1" />
                  <span className="text-[12px] text-muted-foreground">km/h</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="label-mono text-muted-foreground">Durée minimale</span>
                  <input type="number" min="0" max="86400" value={roadMinDur} data-testid="road-minduration-input"
                    onChange={(e) => setRoadMinDur(Math.max(0, Math.min(86400, Number(e.target.value) || 0)))}
                    className="w-24 text-[13px] rounded-md border border-border bg-card px-2 py-1" />
                  <span className="text-[12px] text-muted-foreground">s</span>
                </div>
                <span className="text-[11px] text-muted-foreground italic" data-testid="road-cooldown-note">
                  Le délai entre notifications ne concerne que les alertes et ne masque aucun épisode du rapport.
                </span>
              </div>

              {roadMeta && roadMeta.available === false ? (
                <div className="px-4 py-3 text-[12px] bg-[#F5821F]/10 border border-[#F5821F]/30 rounded-lg text-[#9a4d0f]" data-testid="road-unavailable">
                  Fonctionnalité indisponible : aucun jeu de limitations routières local n'est chargé{roadMeta.reason ? ` (${roadMeta.reason})` : ""}.
                </div>
              ) : (
                <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="road-coverage">
                  <div className="px-4 py-2 text-[11px] bg-secondary/30 border-b border-border flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="road-attribution">
                    <span className="font-semibold text-foreground">{(roadMeta?.attribution || "© OpenStreetMap contributors")} / {(roadMeta?.license || "ODbL")}</span>
                    {roadMeta?.source_date && <span className="text-muted-foreground" data-testid="road-extract-date">Extrait du {roadMeta.source_date}</span>}
                    {roadMeta?.region && <span className="text-muted-foreground">· {roadMeta.region}</span>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border">
                    <div className="bg-card px-4 py-3" data-testid="road-coverage-network">
                      <div className="label-mono text-[#17A2B8] font-bold mb-1">Couverture RÉSEAU (jeu de données)</div>
                      <div className="text-[13px]">{(roadMeta?.segment_count ?? 0).toLocaleString("fr-FR")} tronçons routiers indexés</div>
                      <div className="text-[11px] text-muted-foreground">Limitations issues d'OpenStreetMap, jamais inventées.</div>
                    </div>
                    <div className="bg-card px-4 py-3" data-testid="road-coverage-effective">
                      <div className="label-mono text-[#17A2B8] font-bold mb-1">Couverture EFFECTIVE (positions analysées)</div>
                      {roadCoverage ? (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
                          <span data-testid="road-cov-points">{roadCoverage.points} points</span>
                          <span data-testid="road-cov-matched">{roadCoverage.matched} rattachés</span>
                          <span data-testid="road-cov-known">{roadCoverage.known_limit} limite connue</span>
                          <span className="text-muted-foreground" data-testid="road-cov-unknown">{roadCoverage.unknown} inconnu</span>
                          <span className="text-muted-foreground" data-testid="road-cov-ambiguous">{roadCoverage.ambiguous} ambigu</span>
                          <span className="text-muted-foreground" data-testid="road-cov-unsupported">{roadCoverage.unsupported} non pris en charge</span>
                        </div>
                      ) : <div className="text-[12px] text-muted-foreground">Aucune position analysée sur la période.</div>}
                      <div className="text-[11px] text-muted-foreground mt-1 italic" data-testid="road-excluded-note">
                        Les états « inconnu », « ambigu » et « non pris en charge » ne sont JAMAIS comptés comme excès.
                      </div>
                    </div>
                  </div>
                  {roadParams && (
                    <div className="px-4 py-2 text-[11px] text-muted-foreground border-t border-border" data-testid="road-params-note">
                      Paramètres appliqués — marge : {roadParams.margin_kmh} km/h · durée minimale : {roadParams.min_duration_s} s
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="border border-border rounded-lg overflow-hidden bg-card">
            {sel.src === "energy" && energyKpis && !loading && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 p-3 bg-[#17A2B8]/5 border-b border-border" data-testid="energy-kpis">
                {energyKpisFromSummary(energyKpis).map(([label, val]) => (
                  <div key={label} className="rounded-md bg-card border border-border px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                    <div className="text-base font-bold text-[#17A2B8]">{val}</div>
                  </div>
                ))}
              </div>
            )}
            {sel.src === "poivisits" && poiKpis && !loading && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 p-3 bg-[#17A2B8]/5 border-b border-border" data-testid="poi-kpis">
                {poiVisitsKpiCards(poiKpis).map(([label, val]) => (
                  <div key={label} className="rounded-md bg-card border border-border px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                    <div className="text-base font-bold text-[#17A2B8]">{val}</div>
                  </div>
                ))}
              </div>
            )}
            {isPoiVisits && poiUnsupported > 0 && !loading && (
              <div className="px-4 py-2 text-[12px] bg-[#F5821F]/10 border-b border-[#F5821F]/30 text-[#9a4d0f]" data-testid="poi-unsupported-note">
                {poiUnsupported === 1
                  ? "1 POI non analysé : géométrie tronçon/non exploitable"
                  : `${poiUnsupported} POI non analysés : géométrie tronçon/non exploitable`}
              </div>
            )}
            {isPoiVisits && topPois.length > 0 && !loading && (
              <div className="px-4 py-3 border-b border-border bg-card" data-testid="poi-top-visited">
                <div className="label-mono text-[#17A2B8] font-bold mb-2">Sites les plus visités</div>
                <ol className="space-y-1">
                  {topPois.map((r, i) => (
                    <li key={r.poi_id} className="flex items-baseline gap-2 text-[13px]" data-testid={`poi-top-${i}`}>
                      <span className="text-muted-foreground font-mono w-5 shrink-0">{i + 1}.</span>
                      <span className="font-semibold truncate max-w-[240px]" title={r.poi}>{r.poi}</span>
                      <span className="text-muted-foreground">{poiTopDetail(r)}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {isHw ? (
              <div className="text-center text-muted-foreground py-12 px-6 text-sm flex flex-col items-center gap-2" data-testid="report-hw-note">
                <Cpu className="w-8 h-8 opacity-40" />Ce rapport dépend d'un capteur / matériel (carburant, température, CAN…). Il se remplira une fois le traceur physique raccordé.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-secondary/40"><tr className="label-mono text-left">{enabledCols.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}</tr></thead>
                <tbody className="data">
                  {loading ? (
                    <tr><td colSpan={enabledCols.length || 1} className="text-center text-muted-foreground py-10">Chargement...</td></tr>
                  ) : shownRows.length === 0 ? (
                    <tr><td colSpan={enabledCols.length || 1} className="text-center text-muted-foreground py-10">Aucune donnée sur la période</td></tr>
                  ) : values.resume ? (
                    displayRows.map((r) => (
                      <tr key={r.vehicle_id} className="border-t-2 border-[#17A2B8]/40 bg-[#17A2B8]/10 font-bold" data-testid="report-summary-row">
                        {enabledCols.map((c) => <td key={c.key} className="px-4 py-3">{cell(r, c)}</td>)}
                      </tr>
                    ))
                  ) : (displayRows.map((r) => (
                    <tr key={r.vehicle_id} className="border-t border-border hover:bg-black/5" data-testid={`report-row-${String(r.vehicle_id).replace(/\s/g, "")}`}>
                      {enabledCols.map((c) => <td key={c.key} className="px-4 py-3 max-w-[220px]">{renderCell(r, c)}</td>)}
                    </tr>
                  )))}
                  {hasAgg && shownRows.length > 0 && (
                    <tr className="border-t-2 border-[#17A2B8]/40 bg-[#17A2B8]/10 font-bold" data-testid="report-agg-row">
                      {enabledCols.map((c, i) => <td key={c.key} className="px-4 py-3">{i === 0 ? "Agrégat" : (c.agg && c.agg !== "none" ? aggVal(shownRows, c.key, c.agg) : "")}</td>)}
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {isExces && !excesShowPoints && !loading && !isHw && (
            <div className="mt-5 border border-border rounded-lg overflow-hidden bg-card" data-testid="exces-detail-section">
              <div className="px-4 py-3 bg-[#17A2B8]/5 border-b border-border flex items-center gap-2">
                <span className="label-mono text-[#17A2B8] font-bold">Détail des excès</span>
                <span className="text-xs text-muted-foreground">{episodes.length} épisode{episodes.length > 1 ? "s" : ""}</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-secondary/40"><tr className="label-mono text-left">
                  {EXCES_WEB_HEADER.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
                </tr></thead>
                <tbody className="data">
                  {episodes.length === 0 ? (
                    <tr><td colSpan={EXCES_WEB_HEADER.length} className="text-center text-muted-foreground py-8">Aucun excès de vitesse sur la période</td></tr>
                  ) : episodes.map((ep, i) => {
                    const cells = excesWebRow(ep);
                    return (
                      <tr key={`${ep.vehicle_id}-${i}`} className="border-t border-border hover:bg-black/5" data-testid={`exces-row-${i}`}>
                        {cells.map((val, j) => (
                          <td key={j} className="px-4 py-3 max-w-[220px]">
                            {j === 7
                              ? <span className="inline-block max-w-[200px] truncate align-bottom" title={String(val)} data-testid={`exces-loc-${i}`}>{val}</span>
                              : j === 6
                              ? <span className="inline-flex items-center gap-1.5">
                                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold text-white" style={{ background: TIER_COLOR(ep.tier) }} data-testid={`exces-tier-${i}`}>{val}</span>
                                  {ep.abs_over_140 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ background: "#7C3AED" }} title="Vitesse absolue supérieure à 140 km/h" data-testid={`exces-abs140-${i}`}>&gt;140</span>}
                                </span>
                              : val}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {excesShowPoints && !loading && !isHw && (
            <div className="mt-5 border border-border rounded-lg overflow-hidden bg-card" data-testid="exces-points-section">
              <div className="px-4 py-3 bg-[#17A2B8]/5 border-b border-border flex flex-wrap items-center gap-2">
                <span className="label-mono text-[#17A2B8] font-bold">Toutes les mesures supérieures au seuil</span>
                <span className="text-xs text-muted-foreground" data-testid="exces-points-count">
                  {(pointsInfo?.total || 0)} mesure{(pointsInfo?.total || 0) > 1 ? "s" : ""}
                </span>
              </div>
              {pointsInfo?.truncated && (
                <div className="px-4 py-2 text-[12px] bg-[#F5821F]/10 border-b border-[#F5821F]/30 text-[#9a4d0f]" data-testid="exces-points-truncated">
                  Affichage limité aux {pointsInfo.count} premières mesures sur {pointsInfo.total}. Utilisez l'export CSV pour la liste complète.
                </div>
              )}
              <div className="max-h-[520px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/40 sticky top-0"><tr className="label-mono text-left">
                    {POINTS_WEB_HEADER.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
                  </tr></thead>
                  <tbody className="data">
                    {points.length === 0 ? (
                      <tr><td colSpan={POINTS_WEB_HEADER.length} className="text-center text-muted-foreground py-8">Aucune mesure supérieure au seuil sur la période</td></tr>
                    ) : points.map((p, i) => {
                      const cells = pointWebCells(p);
                      return (
                        <tr key={`${p.vehicle_id}-${p.timestamp}-${i}`} className="border-t border-border hover:bg-black/5" data-testid={`exces-point-row-${i}`}>
                          {cells.map((val, j) => (
                            <td key={j} className="px-4 py-3 max-w-[220px]">
                              {j === 5 ? <span className="font-bold text-[#EF4444]" data-testid={`exces-point-speed-${i}`}>{val} km/h</span>
                                : j === 6 ? <span className="font-bold text-[#F5821F]" data-testid={`exces-point-excess-${i}`}>+{val} km/h</span>
                                : j === 8 ? <span className="inline-flex items-center gap-1.5">
                                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold text-white" style={{ background: TIER_COLOR(p.tier) }} data-testid={`exces-point-tier-${i}`}>{val}</span>
                                    {p.abs_over_140 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ background: "#7C3AED" }} title="Vitesse absolue supérieure à 140 km/h" data-testid={`exces-point-abs140-${i}`}>&gt;140</span>}
                                  </span>
                                : val}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {isPoiVisits && !loading && !isHw && (
            <div className="mt-5 border border-border rounded-lg overflow-hidden bg-card" data-testid="poi-detail-section">
              <div className="px-4 py-3 bg-[#17A2B8]/5 border-b border-border flex items-center gap-2">
                <span className="label-mono text-[#17A2B8] font-bold">Détail des visites</span>
                <span className="text-xs text-muted-foreground">{visits.length} visite{visits.length > 1 ? "s" : ""}</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-secondary/40"><tr className="label-mono text-left">
                  {POI_WEB_HEADER.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
                </tr></thead>
                <tbody className="data">
                  {visits.length === 0 ? (
                    <tr><td colSpan={POI_WEB_HEADER.length} className="text-center text-muted-foreground py-8">Aucune visite POI sur la période</td></tr>
                  ) : visits.map((v, i) => {
                    const cells = visitRow(v);
                    return (
                      <tr key={`${v.vehicle_id}-${i}`} className="border-t border-border hover:bg-black/5" data-testid={`poi-visit-row-${i}`}>
                        {cells.map((val, j) => (
                          <td key={j} className="px-4 py-3 max-w-[220px]">
                            {j === 2
                              ? <span className="inline-block max-w-[200px] truncate align-bottom" title={String(val)} data-testid={`poi-visit-name-${i}`}>{val}</span>
                              : val}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {isRoad && !loading && !isHw && roadMeta?.available !== false && (
            <div className="mt-5 border border-border rounded-lg overflow-hidden bg-card" data-testid="road-detail-section">
              <div className="px-4 py-3 bg-[#17A2B8]/5 border-b border-border flex items-center gap-2">
                <span className="label-mono text-[#17A2B8] font-bold">Détail des excès selon la route</span>
                <span className="text-xs text-muted-foreground">{roadEpisodes.length} épisode{roadEpisodes.length > 1 ? "s" : ""}</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-secondary/40"><tr className="label-mono text-left">
                  {ROAD_WEB_HEADER.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
                </tr></thead>
                <tbody className="data">
                  {roadEpisodes.length === 0 ? (
                    <tr><td colSpan={ROAD_WEB_HEADER.length} className="text-center text-muted-foreground py-8">Aucun excès selon la route sur la période</td></tr>
                  ) : roadEpisodes.map((e, i) => {
                    const cells = roadWebRow(e);
                    return (
                      <tr key={`${e.vehicle_id}-${i}`} className="border-t border-border hover:bg-black/5" data-testid={`road-row-${i}`}>
                        {cells.map((val, j) => (
                          <td key={j} className="px-4 py-3 max-w-[220px]">
                            {j === 6 ? <span className="font-bold text-[#F5821F]" data-testid={`road-excess-${i}`}>{val}</span>
                              : j === 8 ? <span className="inline-block max-w-[200px] truncate align-bottom" title={String(val)} data-testid={`road-loc-${i}`}>{val}</span>
                              : val}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
